import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

export type BookIdeaDetail = {
  id: string;
  title: string;
  description: string;
  genre: string[];
  custom_tags: string[];
  cover_image: string | null;
  created_at: string;
  author_id: string;
  identity_id: string;
  identity_username: string;
  identity_avatar_url: string | null;
  votes_count: number;
  has_voted: boolean;
};

export function useBookIdeaDetail(ideaId: string) {
  const [idea, setIdea] = useState<BookIdeaDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isVoting, setIsVoting] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    fetchIdea();
  }, [ideaId]);

  async function fetchIdea() {
    try {
      const { data, error } = await supabase
        .from('book_ideas')
        .select(`
          *,
          votes:votes(count),
          has_voted:votes(user_id)
        `)
        .eq('id', ideaId)
        .single();

      if (error) throw error;

      setIdea({
        ...data,
        votes_count: data.votes[0]?.count ?? 0,
        has_voted: data.has_voted.some(v => v.user_id === user?.id)
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch book idea');
    } finally {
      setLoading(false);
    }
  }

  const toggleVote = async () => {
    if (!user || !idea || isVoting) return;

    try {
      setIsVoting(true);

      if (idea.has_voted) {
        // Delete vote if it exists
        await supabase
          .from('votes')
          .delete()
          .match({ idea_id: ideaId, user_id: user.id });
      } else {
        // Try to insert vote, ignore if it already exists
        const { error } = await supabase
          .from('votes')
          .upsert(
            { idea_id: ideaId, user_id: user.id },
            { onConflict: 'idea_id,user_id', ignoreDuplicates: true }
          );

        if (error && error.code !== '23505') { // Ignore unique constraint violations
          throw error;
        }
      }

      await fetchIdea();
    } catch (err) {
      console.error('Error toggling vote:', err);
    } finally {
      setIsVoting(false);
    }
  };

  return { idea, loading, error, toggleVote };
}